/**
 * RubricEditor (Plan 04-07 / FIN-02 / POS-07 / BUG-022 / IMP-045 R8).
 *
 * Renders a 0-5 number input for ENABLED rubric dims only (disabled dims hidden per
 * position.rubric_config_json, POS-07), the RED_FLAG_TAGS checkbox set (informational —
 * excluded from the composite), and a verbatim notes textarea. Save (Guardar) → PUT rubric →
 * shows the returned composite_score; Discard (Descartar) resets to the saved values. Every
 * 0-5 input declares HTML min=0 max=5 paired with an inline out-of-range error (BUG-022).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { mapErrorSlug } from "@/lib/error-slug-mapper";

import type { RubricSavePayload, Stage2RubricDetail } from "./api";
import { useSaveRubricMutation } from "./hooks";

/** The four numeric dims (mirrors backend composite_scoring._NUMERIC_DIMS). */
const NUMERIC_DIMS = [
  "language_fluency",
  "confidence",
  "professional_impression",
  "relevance_of_example",
] as const;
type NumericDim = (typeof NUMERIC_DIMS)[number];

/** RED_FLAG_TAGS — mirrors backend composite_scoring.RED_FLAG_TAGS (informational only). */
export const RED_FLAG_TAGS = [
  "inconsistent_story",
  "unprofessional_conduct",
  "dishonesty_signal",
  "unclear_communication",
  "overstated_experience",
  "poor_example_relevance",
] as const;

type RubricConfig = Record<string, { enabled?: boolean; weight?: number }>;

export interface RubricEditorProps {
  candidateId: number;
  positionId: number;
  rubricConfig: Record<string, unknown>;
  savedRubric: Stage2RubricDetail | null;
}

function initialScore(saved: Stage2RubricDetail | null, dim: NumericDim): string {
  const v = saved ? saved[dim] : null;
  return v == null ? "" : String(v);
}

export function RubricEditor(props: RubricEditorProps) {
  const { t } = useTranslation("rubric");
  const cfg = props.rubricConfig as RubricConfig;
  const enabledDims = useMemo(
    () => NUMERIC_DIMS.filter((d) => cfg?.[d]?.enabled === true),
    [cfg],
  );

  const [scores, setScores] = useState<Record<string, string>>(() =>
    Object.fromEntries(NUMERIC_DIMS.map((d) => [d, initialScore(props.savedRubric, d)])),
  );
  const [redFlags, setRedFlags] = useState<Set<string>>(
    () => new Set(props.savedRubric?.red_flags ?? []),
  );
  const [notes, setNotes] = useState(props.savedRubric?.notes ?? "");
  const [composite, setComposite] = useState<number | null>(
    props.savedRubric?.composite_score ?? null,
  );

  const save = useSaveRubricMutation(props.candidateId);

  /** A score is invalid when non-empty AND not an integer in [0,5] (BUG-022). */
  function isOutOfRange(raw: string): boolean {
    if (raw.trim() === "") return false;
    const n = Number(raw);
    return !Number.isInteger(n) || n < 0 || n > 5;
  }
  const anyOutOfRange = enabledDims.some((d) => isOutOfRange(scores[d]));

  function reset() {
    setScores(
      Object.fromEntries(
        NUMERIC_DIMS.map((d) => [d, initialScore(props.savedRubric, d)]),
      ),
    );
    setRedFlags(new Set(props.savedRubric?.red_flags ?? []));
    setNotes(props.savedRubric?.notes ?? "");
  }

  async function handleSave() {
    if (anyOutOfRange) return;
    const payload: RubricSavePayload = {
      language_fluency: null,
      confidence: null,
      professional_impression: null,
      relevance_of_example: null,
      red_flags: [...redFlags],
      notes: notes.trim() === "" ? null : notes,
    };
    // Only enabled dims carry a value; disabled stay null (backend forces null anyway).
    for (const d of enabledDims) {
      const raw = scores[d];
      payload[d] = raw.trim() === "" ? null : Number(raw);
    }
    try {
      const res = await save.mutateAsync(payload);
      setComposite(res.composite_score);
      toast.success(t("rubric.saved"));
    } catch (err) {
      toast.error(mapErrorSlug(err));
    }
  }

  return (
    <section className="space-y-4" data-testid={`rubric-editor-${props.candidateId}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t("rubric.title")}</h3>
        {composite != null ? (
          <span className="text-sm" data-testid="composite-score">
            {t("rubric.composite")}: <strong>{composite.toFixed(1)}</strong>
          </span>
        ) : null}
      </div>

      {/* Enabled numeric dims only (POS-07) */}
      <div className="space-y-3">
        {enabledDims.map((dim) => {
          const bad = isOutOfRange(scores[dim]);
          return (
            <div key={dim} className="space-y-1">
              <Label htmlFor={`rubric-${dim}`} className="text-sm">
                {t(`rubric.dims.${dim}`)}{" "}
                <span className="text-muted-foreground">({t("rubric.score_hint")})</span>
              </Label>
              <Input
                id={`rubric-${dim}`}
                type="number"
                inputMode="numeric"
                min={0}
                max={5}
                step={1}
                value={scores[dim]}
                onChange={(e) =>
                  setScores((s) => ({ ...s, [dim]: e.target.value }))
                }
                aria-invalid={bad}
                className="w-24 text-base"
                data-testid={`rubric-input-${dim}`}
              />
              {bad ? (
                <p className="text-sm text-destructive" data-testid={`rubric-error-${dim}`}>
                  {t("rubric.out_of_range")}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* RED_FLAG_TAGS (informational chips, excluded from composite) */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">{t("rubric.red_flags")}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {RED_FLAG_TAGS.map((tag) => (
            <Label
              key={tag}
              htmlFor={`flag-${tag}`}
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <Checkbox
                id={`flag-${tag}`}
                checked={redFlags.has(tag)}
                onCheckedChange={(v) =>
                  setRedFlags((prev) => {
                    const next = new Set(prev);
                    if (v === true) next.add(tag);
                    else next.delete(tag);
                    return next;
                  })
                }
              />
              {t(`rubric.red_flag_tags.${tag}`)}
            </Label>
          ))}
        </div>
      </div>

      {/* Verbatim notes */}
      <div className="space-y-1">
        <Label htmlFor="rubric-notes" className="text-sm">
          {t("rubric.notes")}
        </Label>
        <Textarea
          id="rubric-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("rubric.notes_placeholder")}
          className="min-h-[80px] text-base"
        />
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleSave}
          disabled={anyOutOfRange || save.isPending}
          className="min-h-[44px]"
          data-testid="rubric-save"
        >
          {t("rubric.save")}
        </Button>
        <Button
          variant="ghost"
          onClick={reset}
          disabled={save.isPending}
          className="min-h-[44px]"
        >
          {t("rubric.discard")}
        </Button>
      </div>
    </section>
  );
}
